import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../../api/admin';
import { authApi } from '../../api/auth';
import { 
  ArrowLeft, Ban, Unlock, DollarSign, Edit, CreditCard, 
  Trophy, TrendingUp, Users, FileText, AlertTriangle, Crown, ChevronLeft, ChevronRight
} from 'lucide-react';
import { formatD10S } from '../../utils/formatters';
import { getTransactionDisplayAmount, getTransactionDisplayCurrency } from '../../utils/transactionDisplay';

const AdminUserDetail = () => {
  const TRANSACTIONS_PER_PAGE = 10;
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview, wallet, activity, cards
  const [transactionsPage, setTransactionsPage] = useState(0);
  const currentUser = authApi.getCurrentUser();
  const canPromote = currentUser?.adminRole === 'SUPER_ADMIN';

  useEffect(() => {
    loadUser();
  }, [id]);

  useEffect(() => {
    setTransactionsPage(0);
  }, [id, userDetail?.transactions?.length]);

  const loadUser = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getUser(id);
      if (response?.success && response.user) {
        setUserDetail(response.user);
        setUser(response.user.user);
      } else {
        setUserDetail(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Error loading user:', error);
      setUserDetail(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBan = async (banType = 'total') => {
    if (!window.confirm(`Are you sure you want to ban this user? Type: ${banType}`)) return;
    
    const reason = window.prompt('Reason for ban:') || 'No reason provided';
    if (reason === null) return; // User cancelled
    
    try {
      const response = await adminApi.banUser(id, { banType, reason });
      if (response?.success) {
        loadUser();
      }
    } catch (error) {
      window.alert('Error banning user: ' + error.message);
    }
  };

  const handleUnban = async (banType = 'total') => {
    if (!window.confirm('Are you sure you want to unban this user?')) return;
    
    try {
      const response = await adminApi.unbanUser(id, { banType });
      if (response?.success) {
        loadUser();
      }
    } catch (error) {
      window.alert('Error unbanning user: ' + error.message);
    }
  };

  const handleAdjustBalance = async () => {
    const amount = window.prompt('Enter amount (positive to add, negative to subtract):');
    if (amount === null) return; // User cancelled
    
    const reason = window.prompt('Reason for adjustment:') || 'Manual adjustment';
    if (reason === null) return; // User cancelled
    
    try {
      const response = await adminApi.adjustBalance(id, { 
        amount: parseFloat(amount), 
        reason 
      });
      if (response?.success) {
        loadUser();
        window.alert('Balance adjusted successfully');
      }
    } catch (error) {
      window.alert('Error adjusting balance: ' + error.message);
    }
  };

  const handlePromoteToAdmin = async () => {
    if (!window.confirm('Promote this user to admin?')) return;

    const roleInput = window.prompt('Role for admin (ADMIN or SUPER_ADMIN):', 'ADMIN');
    if (roleInput === null) return;
    const role = roleInput.trim().toUpperCase() || 'ADMIN';
    if (!['ADMIN', 'SUPER_ADMIN'].includes(role)) {
      window.alert('Invalid role. Use ADMIN or SUPER_ADMIN.');
      return;
    }

    const password = window.prompt('Password (required only if user has no password):', '');
    if (password === null) return;

    try {
      const response = await adminApi.promoteUserToAdmin(user.id, role, password || undefined);
      if (response?.success) {
        window.alert('User promoted to admin successfully');
      }
    } catch (error) {
      window.alert('Error promoting user: ' + error.message);
    }
  };

  const isReferralTransaction = (tx) => {
    const relatedType = String(tx?.related_type || '').toLowerCase();
    const description = String(tx?.description || '').toLowerCase();

    return relatedType === 'referral' || description.includes('referral bonus');
  };

  const getAuctionBidCardId = (tx) => {
    const description = String(tx?.description || '');
    const match = description.match(/^Auction bid on card #(\d+)$/i);
    return match ? match[1] : null;
  };

  const cardsList = userDetail?.cardsList || [];
  const rawTransactions = userDetail?.transactions || [];

  const toTimestamp = (value) => {
    const timestamp = new Date(value || 0).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
  };

  const isSameAmount = (firstAmount, secondAmount) =>
    Math.abs((Number(firstAmount) || 0) - (Number(secondAmount) || 0)) < 0.0001;

  const matchedAuctionTransactionIds = new Set();
  const usedAuctionTransactionIds = new Set();

  cardsList
    .filter((card) => String(card?.acquired_via || '').toLowerCase() === 'auction')
    .sort((cardA, cardB) => toTimestamp(cardB?.acquired_at) - toTimestamp(cardA?.acquired_at))
    .forEach((card) => {
      const auctionCardId = String(card?.user_card_id || card?.id || '');
      const auctionPurchasePrice = Number(card?.purchase_price || 0);
      const auctionAcquiredAt = toTimestamp(card?.acquired_at);

      const candidateTransactions = rawTransactions.filter((tx) => {
        if (usedAuctionTransactionIds.has(tx.id)) {
          return false;
        }

        return (
          String(tx?.type || '').toLowerCase() === 'purchase' &&
          getAuctionBidCardId(tx) === auctionCardId
        );
      });

      if (!candidateTransactions.length) {
        return;
      }

      const samePriceTransactions = auctionPurchasePrice > 0
        ? candidateTransactions.filter((tx) => isSameAmount(tx?.amount, auctionPurchasePrice))
        : [];
      const transactionsToEvaluate = samePriceTransactions.length
        ? samePriceTransactions
        : candidateTransactions;

      const matchedTransaction = transactionsToEvaluate.sort((txA, txB) => {
        const diffA = Math.abs(toTimestamp(txA?.created_at) - auctionAcquiredAt);
        const diffB = Math.abs(toTimestamp(txB?.created_at) - auctionAcquiredAt);
        return diffA - diffB;
      })[0];

      if (matchedTransaction) {
        usedAuctionTransactionIds.add(matchedTransaction.id);
        matchedAuctionTransactionIds.add(matchedTransaction.id);
      }
    });

  const isWonAuctionBidTransaction = (tx) => {
    const auctionBidCardId = getAuctionBidCardId(tx);
    if (!auctionBidCardId) {
      return false;
    }

    return matchedAuctionTransactionIds.has(tx?.id);
  };

  const shouldHideTransaction = (tx) => {
    const type = String(tx?.type || '').toLowerCase();
    const description = String(tx?.description || '').toLowerCase();

    if (description === 'auction bid refund') {
      return true;
    }

    if (type === 'prize' && !isReferralTransaction(tx)) {
      return true;
    }

    if (getAuctionBidCardId(tx) && !isWonAuctionBidTransaction(tx)) {
      return true;
    }

    return false;
  };

  const seenAuctionBidCards = new Set();
  const visibleTransactions = rawTransactions.filter((tx) => {
    if (shouldHideTransaction(tx)) {
      return false;
    }

    const auctionBidCardId = getAuctionBidCardId(tx);
    if (!auctionBidCardId) {
      return true;
    }

    if (seenAuctionBidCards.has(auctionBidCardId)) {
      return false;
    }

    seenAuctionBidCards.add(auctionBidCardId);
    return true;
  });
  const currentTransactionsPageItems = visibleTransactions.slice(
    transactionsPage * TRANSACTIONS_PER_PAGE,
    (transactionsPage + 1) * TRANSACTIONS_PER_PAGE
  );
  const depositTransactions = currentTransactionsPageItems.filter(
    (tx) => String(tx?.type || '').toLowerCase() === 'deposit'
  );
  const referralTransactions = currentTransactionsPageItems.filter((tx) =>
    isReferralTransaction(tx)
  );
  const auctionPurchaseTransactions = currentTransactionsPageItems.filter((tx) =>
    isWonAuctionBidTransaction(tx)
  );
  const otherTransactions = currentTransactionsPageItems.filter(
    (tx) =>
      String(tx?.type || '').toLowerCase() !== 'deposit' &&
      !isReferralTransaction(tx) &&
      !isWonAuctionBidTransaction(tx)
  );
  const rarityOrder = ['Unique', 'Epic', 'Rare', 'Plus', 'Common', 'Unknown'];
  const groupedCardsByRarity = cardsList.reduce((groups, card) => {
    const rarityName = card?.rarity_name || 'Unknown';
    if (!groups[rarityName]) {
      groups[rarityName] = [];
    }
    groups[rarityName].push(card);
    return groups;
  }, {});
  const orderedCardGroups = Object.entries(groupedCardsByRarity).sort(([rarityA], [rarityB]) => {
    const indexA = rarityOrder.indexOf(rarityA);
    const indexB = rarityOrder.indexOf(rarityB);
    const safeIndexA = indexA === -1 ? rarityOrder.length : indexA;
    const safeIndexB = indexB === -1 ? rarityOrder.length : indexB;

    if (safeIndexA !== safeIndexB) {
      return safeIndexA - safeIndexB;
    }

    return rarityA.localeCompare(rarityB);
  });

  const formatTransactionLabel = (type) => {
    if (!type) return 'Transaction';
    return String(type)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getTransactionLabel = (tx) => {
    if (isReferralTransaction(tx)) {
      return 'Referral Bonus';
    }

    return formatTransactionLabel(tx?.type);
  };

  const renderTransactionPagination = (page, setPage, totalItems) => {
    const totalPages = Math.ceil(totalItems / TRANSACTIONS_PER_PAGE);

    if (totalPages <= 1) {
      return null;
    }

    return (
      <div className="flex items-center justify-between mt-3 text-sm text-gray-400">
        <span>
          Page {page + 1} of {totalPages}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((currentPage) => Math.max(0, currentPage - 1))}
            disabled={page === 0}
            className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" />
            Prev
          </button>
          <button
            type="button"
            onClick={() => setPage((currentPage) => Math.min(totalPages - 1, currentPage + 1))}
            disabled={page >= totalPages - 1}
            className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  const renderTransactionsList = (
    transactions,
    emptyText,
    amountClassName = 'text-yellow-400'
  ) => {
    if (!transactions.length) {
      return <div className="text-gray-500 text-sm">{emptyText}</div>;
    }

    return (
      <div>
        <div className="space-y-2">
          {transactions.map((tx) => (
            <div key={tx.id} className="flex justify-between items-center p-3 bg-gray-700 rounded">
              <div>
                <div className="font-semibold">{getTransactionLabel(tx)}</div>
                <div className="text-sm text-gray-400">{new Date(tx.created_at).toLocaleString()}</div>
                {tx.description ? (
                  <div className="text-xs text-gray-500 mt-1">{tx.description}</div>
                ) : null}
              </div>
              <div className={`${amountClassName} font-bold`}>
                {formatD10S(getTransactionDisplayAmount(tx))} {getTransactionDisplayCurrency(tx)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto"></div>
        <p className="mt-4 text-gray-400">Loading user...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">User not found</p>
        <button
          onClick={() => navigate('/admin/users')}
          className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg"
        >
          Back to Users
        </button>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: FileText },
    { id: 'wallet', name: 'Wallet & Movements', icon: DollarSign },
    { id: 'activity', name: 'Game Activity', icon: Trophy },
    { id: 'cards', name: 'Cards', icon: CreditCard },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/users')}
            className="p-2 hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold">{user.username}</h1>
            <p className="text-gray-400">{user.email}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2 flex-wrap gap-2">
          {userDetail?.bans?.length > 0 ? (
            userDetail.bans.map((ban) => (
              <button
                key={ban.id}
                onClick={() => handleUnban(ban.ban_type)}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg flex items-center space-x-2"
              >
                <Unlock className="w-4 h-4" />
                <span>Unban {ban.ban_type}</span>
              </button>
            ))
          ) : (
            <>
              <button
                onClick={() => handleBan('total')}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg flex items-center space-x-2"
              >
                <Ban className="w-4 h-4" />
                <span>Ban User</span>
              </button>
              <button
                onClick={() => handleBan('marketplace')}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 rounded-lg"
              >
                Ban Marketplace
              </button>
              <button
                onClick={() => handleBan('tournaments')}
                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 rounded-lg"
              >
                Ban Tournaments
              </button>
            </>
          )}
          {canPromote && (
            <button
              onClick={handlePromoteToAdmin}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg flex items-center space-x-2"
            >
              <Crown className="w-4 h-4" />
              <span>Promote to Admin</span>
            </button>
          )}
          <button
            onClick={handleAdjustBalance}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center space-x-2"
          >
            <DollarSign className="w-4 h-4" />
            <span>Adjust Balance</span>
          </button>
        </div>
      </div>

      {/* User Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-gray-400 text-sm mb-1">Status</div>
          <div className={`text-lg font-semibold ${
            user.is_active && !(userDetail?.bans?.length > 0) ? 'text-green-400' : 'text-red-400'
          }`}>
            {userDetail?.bans?.length > 0 ? 'Banned' : user.is_active ? 'Active' : 'Inactive'}
          </div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-gray-400 text-sm mb-1">KYC Status</div>
          <div className={`text-lg font-semibold ${
            user.is_verified ? 'text-blue-400' : 'text-gray-400'
          }`}>
            {user.is_verified ? 'Verified' : 'Not Verified'}
          </div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-gray-400 text-sm mb-1">Balance</div>
          <div className="text-lg font-semibold text-yellow-400">
            {formatD10S(user.d10s_balance || 0)} DIOS
          </div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-gray-400 text-sm mb-1">Referral Code</div>
          <div className="text-lg font-semibold text-white">
            {user.referral_code || 'N/A'}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 mb-6 border-b border-gray-700">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-400 text-blue-400'
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.name}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        {activeTab === 'overview' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Account Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-gray-400 text-sm">User ID</div>
                  <div className="text-white">{user.id}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-sm">Registration Date</div>
                  <div className="text-white">{new Date(user.created_at).toLocaleDateString()}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-sm">Last Login</div>
                  <div className="text-white">{user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-sm">Language</div>
                  <div className="text-white">{user.language || 'en'}</div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">Referral Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-gray-400 text-sm">Referred By</div>
                  <div className="text-white">{user.referred_by_username || 'None'}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-sm">Total Referrals</div>
                  <div className="text-white">{user.total_referrals || 0}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'wallet' && (
          <div>
            <h3 className="text-lg font-semibold mb-4">Wallet Movements</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center p-3 bg-gray-700 rounded">
                <div>
                  <div className="font-semibold">Available Balance</div>
                  <div className="text-sm text-gray-400">{formatD10S(user.d10s_balance || 0)} DIOS</div>
                </div>
                <div className="text-yellow-400 font-bold">{formatD10S(user.d10s_balance || 0)} DIOS</div>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-700 rounded">
                <div>
                  <div className="font-semibold">Locked Balance (Payroll 1x)</div>
                  <div className="text-sm text-gray-400">Cannot withdraw until spent</div>
                </div>
                <div className="text-orange-400 font-bold">
                  {formatD10S(Math.max(0, (user.total_deposited || 0) - (user.total_spent || 0)))} DIOS
                </div>
              </div>
              <div className="mt-4">
                <div className="text-gray-400 text-sm mb-2">
                  Transactions Page {transactionsPage + 1}
                </div>
                {visibleTransactions.length ? (
                  <>
                    {depositTransactions.length > 0 ? (
                      <div className="mb-6">
                        <div className="text-gray-400 text-sm mb-2">Deposits</div>
                        {renderTransactionsList(
                          depositTransactions,
                          'No deposit transactions found',
                          'text-green-400'
                        )}
                      </div>
                    ) : null}
                    {referralTransactions.length > 0 ? (
                      <div className="mb-6">
                        <div className="text-gray-400 text-sm mb-2">Referral Rewards</div>
                        {renderTransactionsList(
                          referralTransactions,
                          'No referral rewards found',
                          'text-blue-400'
                        )}
                      </div>
                    ) : null}
                    {auctionPurchaseTransactions.length > 0 ? (
                      <div className="mb-6">
                        <div className="text-gray-400 text-sm mb-2">Auction Card Purchases</div>
                        {renderTransactionsList(
                          auctionPurchaseTransactions,
                          'No auction card purchases found',
                          'text-yellow-400'
                        )}
                      </div>
                    ) : null}
                    {otherTransactions.length > 0 ? (
                      <div>
                        <div className="text-gray-400 text-sm mb-2">Other Transactions</div>
                        {renderTransactionsList(
                          otherTransactions,
                          'No other transactions found',
                          'text-yellow-400'
                        )}
                      </div>
                    ) : null}
                    {renderTransactionPagination(
                      transactionsPage,
                      setTransactionsPage,
                      visibleTransactions.length
                    )}
                  </>
                ) : (
                  <div className="text-gray-500 text-sm">No transactions found</div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div>
            <h3 className="text-lg font-semibold mb-4">Game Activity</h3>
            <div className="space-y-4">
              <div className="p-4 bg-gray-700 rounded">
                <div className="font-semibold mb-2">Tournaments Participated</div>
                <div className="text-gray-400">{user.tournaments_count || 0} tournaments</div>
              </div>
              <div className="p-4 bg-gray-700 rounded">
                <div className="font-semibold mb-2">Prizes Won</div>
                <div className="text-gray-400">{formatD10S(user.prizes_won || 0)} DIOS</div>
              </div>
              {userDetail?.tournamentsList?.length ? (
                <div className="space-y-2">
                  {userDetail.tournamentsList.slice(0, 10).map((tournament) => (
                    <div key={tournament.id} className="flex justify-between items-center p-3 bg-gray-700 rounded">
                      <div>
                        <div className="font-semibold">{tournament.name}</div>
                        <div className="text-sm text-gray-400">
                          Rank: {tournament.final_rank ?? 'N/A'} • Score: {tournament.final_score ?? 'N/A'}
                        </div>
                      </div>
                      <div className="text-yellow-400 font-bold">
                        {formatD10S(tournament.prize_won || 0)} DIOS
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500 text-sm">No tournaments found</div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'cards' && (
          <div>
            <h3 className="text-lg font-semibold mb-4">User Cards</h3>
            {cardsList.length ? (
              <div className="space-y-6">
                {orderedCardGroups.map(([rarityName, cards]) => (
                  <div key={rarityName}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-md font-semibold text-white">{rarityName}</h4>
                      <span className="text-sm text-gray-400">{cards.length} cards</span>
                    </div>
                    <div className="space-y-2">
                      {cards.map((card) => (
                        <div key={`${card.id}-${card.rarity_name}`} className="flex justify-between items-center p-3 bg-gray-700 rounded">
                          <div>
                            <div className="font-semibold">{card.full_name || card.display_name || `${card.first_name} ${card.last_name}`}</div>
                            <div className="text-sm text-gray-400">{card.position || 'N/A'}</div>
                          </div>
                          <div className="text-gray-400 text-sm">XP {card.xp_level || 0}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 text-sm">No cards found</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUserDetail;

